import { useState, useEffect } from 'react';
import { WPMedia } from '../../types/wordpress';
import { wordpressAPI } from '../../services/wordpressApi';
import { DocumentArrowDownIcon, EyeIcon } from '@heroicons/react/24/outline';
import LoadingSkeleton from '../ui/LoadingSkeleton';
import ErrorState from '../ui/ErrorState';
import PDFViewer from '../pdf/PDFViewer';
import {
  filterDocumentAttachments,
  getAttachmentFormat,
  isPdfAttachment,
  attachmentLabel,
} from '../../utils/resourceAttachments';
import { decodeHtmlEntities } from '../../utils/htmlEntities';

interface ResourceAttachmentsProps {
  postId: number;
  /** Body HTML. Files already linked there are not listed again. */
  contentHtml?: string;
}

function alreadyLinked(html: string, url: string): boolean {
  if (html.includes(url)) return true;
  try {
    return html.includes(new URL(url).pathname);
  } catch {
    return false;
  }
}

export default function ResourceAttachments({ postId, contentHtml = '' }: ResourceAttachmentsProps) {
  const [attachments, setAttachments] = useState<WPMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [viewingPdf, setViewingPdf] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    const fetchAttachments = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await wordpressAPI.fetchAttachments(postId);
        setAttachments(
          filterDocumentAttachments(data).filter((attachment) => !alreadyLinked(contentHtml, attachment.source_url))
        );
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load attachments'));
      } finally {
        setLoading(false);
      }
    };

    fetchAttachments();
  }, [postId, contentHtml]);

  if (loading) {
    return (
      <div className="mt-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Attachments</h3>
        <LoadingSkeleton lines={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Attachments</h3>
        <ErrorState error={error} />
      </div>
    );
  }

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Attachments</h3>
      <div className="space-y-3">
        {attachments.map((attachment) => {
          const { label, icon: Icon } = getAttachmentFormat(attachment);
          const title = decodeHtmlEntities(attachment.title.rendered) || 'Untitled Document';
          const isPdf = isPdfAttachment(attachment);

          return (
            <a
              key={attachment.id}
              href={attachment.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all group"
            >
              <div className="flex-shrink-0">
                <Icon className="h-8 w-8 text-gray-400 group-hover:text-primary transition-colors" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 group-hover:text-primary transition-colors">
                  {title}
                </p>
                <p className="text-sm text-gray-500">{label}</p>
              </div>
              <div className="flex items-center gap-2">
                {isPdf && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setViewingPdf({
                        url: attachment.source_url,
                        title: title || attachmentLabel(attachment),
                      });
                    }}
                    className="p-2 text-gray-400 hover:text-primary transition-colors rounded-md hover:bg-gray-50"
                    aria-label="View PDF"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                )}
                <DocumentArrowDownIcon className="h-5 w-5 text-gray-400 group-hover:text-primary transition-colors" />
              </div>
            </a>
          );
        })}
      </div>

      {viewingPdf && (
        <PDFViewer
          url={viewingPdf.url}
          title={viewingPdf.title}
          isOpen={!!viewingPdf}
          onClose={() => setViewingPdf(null)}
        />
      )}
    </div>
  );
}
